import React from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import { resolveFillOpacity } from "../../../../utils/fillOpacity";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import {
  DEFAULT_FLOOR_THICKNESS,
  Floor3DShapeDefinition,
} from "./floorGeometry";
import { resolveMaterial } from "../../materials/materialResolver";
import {
  getBuiltInFurnitureMaterial,
  isGlassBuiltInFurnitureMaterial,
} from "../../materials/builtInFurnitureMaterials";
import CatalogStandardMaterial from "../../materials/CatalogStandardMaterial";
import LitPatternMaterial from "../../materials/LitPatternMaterial";
import { WALL_FLOOR_CATALOG_TEXTURE_SIZE } from "../../materials/textureCache";
import ExtrudedShapeMesh from "../../scene/ExtrudedShapeMesh";
import HiddenOccluderEdges from "../../scene/HiddenOccluderEdges";
import { disablePointerRaycast } from "../../interaction/disablePointerRaycast";

const FLOOR_CLICK_MOVE_THRESHOLD_PX = 2;
const FLOOR_SELECTION_SURFACE_Z = 0.001;

const Floor3DMeshes: React.FC<{
  floors: Floor3DShapeDefinition[];
  selectedIds?: Set<string>;
  hiddenFloorIds?: Set<string>;
  panning: boolean;
  onSelectAreaFill?: (
    floor: Floor3DShapeDefinition,
    event?: ThreeEvent<PointerEvent>,
  ) => void;
  onSelectWorkspace?: (event?: ThreeEvent<PointerEvent>) => void;
}> = ({
  floors,
  selectedIds,
  hiddenFloorIds,
  panning,
  onSelectAreaFill,
  onSelectWorkspace,
}) => {
  const pointerDownRef = React.useRef<{
    floorId: string;
    pointerId: number;
    clientX: number;
    clientY: number;
    moved: boolean;
  } | null>(null);

  const handlePointerDown = (
    floor: Floor3DShapeDefinition,
    event: ThreeEvent<PointerEvent>,
  ) => {
    if (event.button !== 0) return;
    if (floor.source !== "areaFill" && !onSelectWorkspace) return;
    if (!isPrimaryFloorPointerTarget(floor, event)) return;

    pointerDownRef.current = {
      floorId: floor.id,
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      moved: false,
    };
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    const pointerDown = pointerDownRef.current;
    if (!pointerDown || pointerDown.pointerId !== event.pointerId) return;

    const movedFarEnough =
      Math.abs(event.clientX - pointerDown.clientX) >
        FLOOR_CLICK_MOVE_THRESHOLD_PX ||
      Math.abs(event.clientY - pointerDown.clientY) >
        FLOOR_CLICK_MOVE_THRESHOLD_PX;
    if (movedFarEnough) {
      pointerDown.moved = true;
    }
  };

  const handlePointerUp = (
    floor: Floor3DShapeDefinition,
    event: ThreeEvent<PointerEvent>,
  ) => {
    const pointerDown = pointerDownRef.current;
    if (
      !pointerDown ||
      pointerDown.floorId !== floor.id ||
      pointerDown.pointerId !== event.pointerId
    ) {
      return;
    }

    pointerDownRef.current = null;
    if (shouldIgnoreFloorPointerSelection(floor, event)) return;

    const movedFarEnough =
      pointerDown.moved ||
      Math.abs(event.clientX - pointerDown.clientX) >
        FLOOR_CLICK_MOVE_THRESHOLD_PX ||
      Math.abs(event.clientY - pointerDown.clientY) >
        FLOOR_CLICK_MOVE_THRESHOLD_PX;

    if (movedFarEnough) return;

    const action = getFloor3DClickAction(floor);

    if (!panning) {
      if (action === "select-area-fill") {
        onSelectAreaFill?.(floor, event);
      } else {
        onSelectWorkspace?.(event);
      }
      return;
    }

    window.setTimeout(() => {
      if (action === "select-area-fill") {
        onSelectAreaFill?.(floor);
      } else {
        onSelectWorkspace?.();
      }
    }, 0);
  };

  return (
    <>
      {floors.map((floor, index) => {
        const selectable = floor.source === "areaFill";
        const cameraHidden = hiddenFloorIds?.has(floor.id);

        if (cameraHidden) {
          return (
            <HiddenOccluderEdges
              key={`floor-edge-${floor.id}-${index}`}
              shape={floor.shape}
              mode="horizontal"
              z={0}
              userData={{
                floorplannerType: "floor-edge",
                floorplannerId: floor.id,
                floorplannerSource: floor.source,
              }}
            />
          );
        }

        return (
          <React.Fragment key={`floor-${floor.id}-${index}`}>
            <ExtrudedShapeMesh
              shape={floor.shape}
              depth={DEFAULT_FLOOR_THICKNESS}
              positionZ={-DEFAULT_FLOOR_THICKNESS}
              castShadow={false}
              receiveShadow
              selected={selectable && selectedIds?.has(floor.id)}
              raycast={disablePointerRaycast}
              userData={{
                floorplannerType: "floor-visual",
                floorplannerId: floor.id,
                floorplannerSource: floor.source,
              }}
            >
              <Floor3DMaterial floor={floor} />
            </ExtrudedShapeMesh>
            <Floor3DSelectionSurface
              floor={floor}
              onPointerDown={(event) => handlePointerDown(floor, event)}
              onPointerMove={handlePointerMove}
              onPointerUp={(event) => handlePointerUp(floor, event)}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

const Floor3DSelectionSurface: React.FC<{
  floor: Floor3DShapeDefinition;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
}> = ({ floor, onPointerDown, onPointerMove, onPointerUp }) => {
  const { gl } = useThree();
  const selectionShape = getFloor3DSelectionShape(floor);
  const geometry = React.useMemo(
    () => createFloor3DSelectionGeometry(selectionShape),
    [selectionShape],
  );

  React.useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <mesh
      geometry={geometry}
      position={[0, 0, FLOOR_SELECTION_SURFACE_Z]}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerOver={(event) => {
        if (!isPrimaryFloorPointerTarget(floor, event)) return;
        gl.domElement.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        gl.domElement.style.cursor = "default";
      }}
      userData={{
        floorplannerType: "floor",
        floorplannerId: floor.id,
        floorplannerSource: floor.source,
      }}
    >
      <meshBasicMaterial
        transparent
        opacity={0}
        colorWrite={false}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

export function getFloor3DSelectionShape(
  floor: Pick<Floor3DShapeDefinition, "shape" | "selectionShape">,
): THREE.Shape {
  return floor.selectionShape ?? floor.shape;
}

export function createFloor3DSelectionGeometry(
  shape: THREE.Shape,
): THREE.ShapeGeometry {
  const geometry = new THREE.ShapeGeometry(shape);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function isPrimaryFloorPointerTarget(
  floor: Pick<Floor3DShapeDefinition, "id">,
  event: Pick<ThreeEvent<PointerEvent>, "intersections">,
): boolean {
  const firstFloorTarget = (event.intersections ?? [])
    .map(({ object }) => getFloorplannerHitTarget(object))
    .find((hitTarget) => hitTarget?.type === "floor");

  return !firstFloorTarget || firstFloorTarget.id === floor.id;
}

export function shouldIgnoreFloorPointerSelection(
  floor: Pick<Floor3DShapeDefinition, "id">,
  event: Pick<ThreeEvent<PointerEvent>, "intersections">,
): boolean {
  const intersections = event.intersections ?? [];
  const floorIndex = intersections.findIndex(({ object }) => {
    const hitTarget = getFloorplannerHitTarget(object);
    return hitTarget?.type === "floor" && hitTarget.id === floor.id;
  });

  if (floorIndex <= 0) return false;

  return intersections.slice(0, floorIndex).some(({ object }) => {
    const hitTarget = getFloorplannerHitTarget(object);
    return Boolean(hitTarget && hitTarget.type !== "floor-edge");
  });
}

function getFloorplannerHitTarget(
  object: THREE.Object3D,
): { type: unknown; id: unknown } | null {
  let current: THREE.Object3D | null = object;
  while (current) {
    const type = current.userData?.floorplannerType;
    if (type) {
      return {
        type,
        id: current.userData?.floorplannerId,
      };
    }
    current = current.parent;
  }
  return null;
}

const Floor3DMaterial: React.FC<{ floor: Floor3DShapeDefinition }> = observer(
  ({ floor }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const defaultColor = resolveMaterial("floor").color;
    const areaFillSymbol =
      floor.source === "areaFill"
        ? floorplannerStore.symbolsMap.get(floor.id)
        : undefined;
    const surfaceRef = areaFillSymbol?.threeD?.surfaceRef;
    const assignedBuiltIn = getBuiltInFurnitureMaterial(surfaceRef?.id);
    const assignedIsGlass = isGlassBuiltInFurnitureMaterial(assignedBuiltIn);
    const defaults = assignedBuiltIn
      ? {
          roughness: assignedBuiltIn.roughness,
          metalness: assignedBuiltIn.metalness,
        }
      : floorplannerStore.threeDMaterialDefaults.floor;

    if (floor.source !== "areaFill") {
      return (
        <CatalogStandardMaterial
          color={defaultColor}
          defaults={defaults}
          side={THREE.DoubleSide}
          maxTextureSize={WALL_FLOOR_CATALOG_TEXTURE_SIZE}
          forceTextureWidth
        />
      );
    }

    const resolvedFloor = resolveMaterial("floor", areaFillSymbol, {
      defaultColor: floor.fillColor ?? defaultColor,
    });
    const topColor = surfaceRef
      ? resolvedFloor.color.getHex()
      : (floor.fillColor ?? defaultColor.getHex());
    const sideColor = new THREE.Color(topColor).multiplyScalar(0.82);
    const fillOpacity = resolveFillOpacity(floor);
    const fillIsTransparent = fillOpacity < 1;

    return (
      <>
        {surfaceRef && assignedIsGlass && assignedBuiltIn ? (
          <meshPhysicalMaterial
            attach="material-0"
            color={topColor}
            roughness={surfaceRef.roughness ?? assignedBuiltIn.roughness}
            metalness={0}
            transmission={assignedBuiltIn.transmission ?? 1}
            thickness={assignedBuiltIn.thickness ?? 0.05}
            ior={assignedBuiltIn.ior ?? 1.5}
            transparent
            opacity={fillOpacity < 1 ? fillOpacity : 1}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        ) : surfaceRef ? (
          <CatalogStandardMaterial
            key={surfaceRef.id}
            attach="material-0"
            color={topColor}
            surfaceRef={surfaceRef}
            defaults={defaults}
            side={THREE.DoubleSide}
            opacity={fillOpacity}
            transparent={fillIsTransparent}
            depthWrite={!fillIsTransparent}
            maxTextureSize={WALL_FLOOR_CATALOG_TEXTURE_SIZE}
            forceTextureWidth
          />
        ) : floor.fillPattern ? (
          <LitPatternMaterial
            attach="material-0"
            fillColor={topColor}
            fillPattern={floor.fillPattern}
            roughness={defaults?.roughness ?? 0.78}
            metalness={defaults?.metalness ?? 0}
            side={THREE.DoubleSide}
            opacity={fillOpacity}
            transparent={fillIsTransparent}
            depthWrite={!fillIsTransparent}
          />
        ) : (
          <CatalogStandardMaterial
            key="floor-default"
            attach="material-0"
            color={topColor}
            surfaceRef={surfaceRef}
            defaults={defaults}
            side={THREE.DoubleSide}
            opacity={fillOpacity}
            transparent={fillIsTransparent}
            depthWrite={!fillIsTransparent}
            maxTextureSize={WALL_FLOOR_CATALOG_TEXTURE_SIZE}
            forceTextureWidth
          />
        )}
        <meshStandardMaterial
          attach="material-1"
          color={sideColor}
          roughness={0.84}
          metalness={0}
          side={THREE.DoubleSide}
          opacity={fillOpacity}
          transparent={fillIsTransparent}
          depthWrite={!fillIsTransparent}
        />
      </>
    );
  },
);

export function getFloor3DClickAction(
  floor: Pick<Floor3DShapeDefinition, "source">,
): "select-area-fill" | "select-workspace" {
  return floor.source === "areaFill" ? "select-area-fill" : "select-workspace";
}

export default Floor3DMeshes;
